import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react/dist/iconify.js";
import { formatDate } from "../helper/DateHelper";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import StandardDatePicker from "./StandardDatePicker";
import { getExposByDate, getExpoOverview, getExpoManagerStats } from "../Apis/ReportAPIs";
import { Accordion } from "react-bootstrap";

const GeneralReportLayer = () => {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [expos, setExpos] = useState([]);
  const [selectedExpoId, setSelectedExpoId] = useState("");
  
  const [expoOverview, setExpoOverview] = useState(null);
  const [loadingExpos, setLoadingExpos] = useState(false);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [managerStats, setManagerStats] = useState({});
  const [loadingStats, setLoadingStats] = useState({});

  useEffect(() => {
    const fetchExpos = async () => {
      if (!fromDate || !toDate) {
        setExpos([]);
        return;
      }
      setLoadingExpos(true);
      try {
        const res = await getExposByDate({ startDate: fromDate, endDate: toDate });
        if (res.status) {
          const data = res.response?.data || [];
          setExpos(data.map(e => ({
            value: e._id,
            label: `${e.expoName} - (${e.location?.name || e.venue || "No Location"})`
          })));
        }
      } catch (error) {
        console.error("Error fetching expos:", error);
      } finally {
        setLoadingExpos(false);
      }
    };
    fetchExpos();
  }, [fromDate, toDate]);

  useEffect(() => {
    const fetchOverview = async () => {
      if (!selectedExpoId) {
        setExpoOverview(null);
        return;
      }
      setLoadingOverview(true);
      try {
        const res = await getExpoOverview(selectedExpoId);
        if (res.status) {
          setExpoOverview(res.response?.data);
          setManagerStats({}); // reset manager stats
        }
      } catch (error) {
        toast.error("Failed to fetch expo overview.");
      } finally {
        setLoadingOverview(false);
      }
    };
    fetchOverview();
  }, [selectedExpoId]);

  const loadManagerStats = async (managerId) => {
    if (managerStats[managerId]) return; // already loaded
    
    setLoadingStats(prev => ({ ...prev, [managerId]: true }));
    try {
      const res = await getExpoManagerStats(selectedExpoId, managerId);
      if (res.status) {
        setManagerStats(prev => ({ ...prev, [managerId]: res.response?.data }));
      }
    } catch (error) {
      toast.error("Failed to load manager stats.");
    } finally {
      setLoadingStats(prev => ({ ...prev, [managerId]: false }));
    }
  };

  return (
    <div className="card h-100 p-0 radius-12 overflow-hidden border-0 shadow-sm">
      <div
        className="card-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between"
        style={{ background: "linear-gradient(135deg, #000 0%, #bb0007 50%, #ed1c24 100%)" }}
      >
        <h6 className="text-white mb-0 fw-bold">Overall Event Report</h6>
      </div>

      <div className="card-body p-24">
        {/* Date & Expo Selection */}
        <div className="bg-light p-20 radius-12 border mb-24 shadow-sm">
          <div className="row gy-3 align-items-end">
            <div className="col-lg-3 col-md-4">
              <label className="mb-1 fw-bold text-dark text-xs uppercase letter-spacing-1">Expo From Date</label>
              <StandardDatePicker 
                value={fromDate} 
                onChange={(e) => setFromDate(e.target.value)} 
                placeholder="Start Date" 
              />
            </div>
            <div className="col-lg-3 col-md-4">
              <label className="mb-1 fw-bold text-dark text-xs uppercase letter-spacing-1">Expo To Date</label>
              <StandardDatePicker 
                value={toDate} 
                onChange={(e) => setToDate(e.target.value)} 
                placeholder="End Date" 
              />
            </div>
            <div className="col-lg-6 col-md-4">
              <label className="mb-1 fw-bold text-dark text-xs uppercase letter-spacing-1">
                Select Expo {loadingExpos && <span className="ms-2 spinner-border spinner-border-sm text-danger" role="status"></span>}
              </label>
              <Select
                options={expos}
                value={expos.find((opt) => opt.value === selectedExpoId) || null}
                onChange={(sel) => setSelectedExpoId(sel ? sel.value : "")}
                styles={selectStyles({ compact: true })}
                placeholder={expos.length > 0 ? "Search and select an Expo" : "Select dates first..."}
                isDisabled={expos.length === 0}
                isClearable
              />
            </div>
          </div>
        </div>

        {/* Expo Overview */}
        {loadingOverview ? (
          <div className="text-center py-48">
            <div className="spinner-border text-danger" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-secondary fw-medium">Loading Expo Details...</p>
          </div>
        ) : expoOverview ? (
          <div>
            <div className="row mb-24">
              <div className="col-md-6 mb-3 mb-md-0">
                <div className="p-20 radius-12 border shadow-sm bg-white h-100">
                  <h5 className="fw-bold text-danger mb-3 border-bottom pb-2">Expo Details</h5>
                  <h6 className="fw-bold mb-2">{expoOverview.expoDetails.expoName}</h6>
                  <div className="d-flex align-items-center gap-2 mb-2 text-secondary-light">
                    <Icon icon="solar:map-point-bold" className="text-danger" />
                    <span className="fw-medium">{expoOverview.expoDetails.venue}</span>
                  </div>
                  <div className="d-flex align-items-center gap-2 text-secondary-light">
                    <Icon icon="solar:calendar-bold" className="text-danger" />
                    <span className="fw-medium">
                      {formatDate(expoOverview.expoDetails.startDate)} - {formatDate(expoOverview.expoDetails.endDate)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="p-20 radius-12 border shadow-sm h-100" style={{ background: "linear-gradient(135deg, #fff9f9, #fff)" }}>
                  <h5 className="fw-bold text-dark mb-3 border-bottom pb-2">Overall Counts</h5>
                  <div className="row text-center mt-4">
                    <div className="col-6 border-end">
                      <div className="display-5 fw-bold text-danger mb-1">{expoOverview.totalStallsBooked}</div>
                      <div className="text-secondary fw-semibold text-uppercase text-xs letter-spacing-1">Total Stalls Booked</div>
                    </div>
                    <div className="col-6">
                      <div className="display-5 fw-bold text-primary mb-1">{expoOverview.totalVisitors}</div>
                      <div className="text-secondary fw-semibold text-uppercase text-xs letter-spacing-1">Total Visitors</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {expoOverview.managerSummary && expoOverview.managerSummary.length > 0 && (
              <div className="table-responsive border radius-8 bg-white overflow-x-auto shadow-sm mb-24">
                <h6 className="fw-bold px-16 py-12 mb-0 border-bottom" style={{ backgroundColor: "#f8f9fa", fontSize: "14px", color: "#333" }}>Manager Status Summary</h6>
                <table className="table bordered-table sm-table mb-0">
                  <thead>
                    <tr style={{ backgroundColor: "#f1f5f9" }}>
                      <th scope="col" className="fw-bold py-12 text-secondary-light uppercase text-xs" style={{ backgroundColor: "#f8f9fa" }}>Manager Name</th>
                      <th scope="col" className="fw-bold py-12 text-center text-secondary-light uppercase text-xs" style={{ backgroundColor: "#f8f9fa" }}>Assigned</th>
                      <th scope="col" className="fw-bold py-12 text-center text-secondary-light uppercase text-xs" style={{ backgroundColor: "#f8f9fa" }}>RNR</th>
                      <th scope="col" className="fw-bold py-12 text-center text-secondary-light uppercase text-xs" style={{ backgroundColor: "#f8f9fa" }}>Follow Ups</th>
                      <th scope="col" className="fw-bold py-12 text-center text-secondary-light uppercase text-xs" style={{ backgroundColor: "#f8f9fa" }}>Prospects</th>
                      <th scope="col" className="fw-bold py-12 text-center text-secondary-light uppercase text-xs" style={{ backgroundColor: "#f8f9fa" }}>Not Interested</th>
                      <th scope="col" className="fw-bold py-12 text-center text-secondary-light uppercase text-xs" style={{ backgroundColor: "#f8f9fa" }}>Blocked</th>
                      <th scope="col" className="fw-bold py-12 text-center text-secondary-light uppercase text-xs" style={{ backgroundColor: "#f8f9fa" }}>Booked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expoOverview.managerSummary.map((item, idx) => (
                      <tr key={idx}>
                        <td className="text-sm fw-medium px-16">{item.managerName || 'Admin / Direct Entry'}</td>
                        <td className="text-center text-sm fw-bold">{item.Assigns}</td>
                        <td className="text-center text-sm fw-bold">{item.RNR}</td>
                        <td className="text-center text-sm fw-bold">{item.Followups}</td>
                        <td className="text-center text-sm fw-bold">{item.Prospects}</td>
                        <td className="text-center text-sm fw-bold">{item.NotInterested}</td>
                        <td className="text-center text-sm fw-bold">{item.Blocked}</td>
                        <td className="text-center text-sm fw-bold">{item.Booked}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <h5 className="fw-bold text-dark mb-16">Event Managers Assigned</h5>
            {expoOverview.managers?.length > 0 ? (
              <Accordion>
                {expoOverview.managers.map((manager, index) => (
                  <Accordion.Item eventKey={index.toString()} key={manager._id} className="mb-16 border radius-8 shadow-sm overflow-hidden">
                    <Accordion.Header onClick={() => loadManagerStats(manager._id)}>
                      <div className="d-flex align-items-center justify-content-between w-100 pe-3">
                        <div className="d-flex align-items-center gap-3">
                          <div className="bg-danger-focus text-danger-main d-flex justify-content-center align-items-center rounded-circle" style={{ width: "40px", height: "40px" }}>
                            <Icon icon="solar:user-bold" className="text-xl" />
                          </div>
                          <div>
                            <span className="text-sm fw-bold mb-0 text-dark d-block">{manager.name}</span>
                            <span className="text-xs text-secondary-light fw-medium">Event Manager</span>
                          </div>
                        </div>
                        <div className="text-end me-4">
                          <div className="fw-bold text-dark fs-5">{manager.clientCount}</div>
                          <div className="text-xs text-secondary text-uppercase fw-semibold">Assigned Clients</div>
                        </div>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body className="bg-light">
                      {loadingStats[manager._id] ? (
                        <div className="text-center py-20">
                          <div className="spinner-border spinner-border-sm text-danger" role="status"></div>
                          <span className="ms-2 fw-medium text-secondary">Loading statistics...</span>
                        </div>
                      ) : managerStats[manager._id] ? (
                        <div className="row text-center py-2">
                          <div className="col-md-4 border-end">
                            <h4 className="fw-bold text-dark mb-1">{managerStats[manager._id].managerStallsBooked}</h4>
                            <span className="text-secondary text-xs text-uppercase fw-semibold letter-spacing-1">Stalls Booked by Manager</span>
                          </div>
                          <div className="col-md-4 border-end">
                            <h4 className="fw-bold text-dark mb-1">{managerStats[manager._id].totalSqm} Sqm</h4>
                            <span className="text-secondary text-xs text-uppercase fw-semibold letter-spacing-1">Total Area Booked</span>
                          </div>
                          <div className="col-md-4">
                            <h4 className="fw-bold text-success-600 mb-1">
                              ₹{managerStats[manager._id].totalAmount ? managerStats[manager._id].totalAmount.toLocaleString('en-IN') : 0}
                            </h4>
                            <span className="text-secondary text-xs text-uppercase fw-semibold letter-spacing-1">Net Amount Generated</span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-10 text-secondary">Stats could not be loaded.</div>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            ) : (
              <div className="alert alert-warning text-center border-0 shadow-sm" role="alert">
                No Event Managers are assigned to this Expo.
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-48 text-secondary border border-dashed radius-12 bg-light">
            <Icon icon="solar:document-text-line-duotone" className="display-4 text-secondary-light mb-16" />
            <h5 className="fw-semibold">No Expo Selected</h5>
            <p className="mb-0">Select a date range and choose an Expo to view the overall report.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralReportLayer;
